/**
 * Create/edit an Application Group (V28).
 *
 * A group is a named set of apps that a policy rule can target as one — the
 * WHAT-side peer of an identity group. Name + description + a member picker over
 * the tenant's apps. The parent keys the modal per target, so the form is seeded
 * at mount (no reset-in-effect, which the repo's React-Compiler lint bans).
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import Modal from '../common/Modal';
import AccessFormField from './AccessFormField';
import { zodResolver } from '../../utils/zodResolver';
import type { AccessApp, AppGroup, AppGroupData } from '../../services/access.service';

const groupSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .max(128)
    .regex(/^[a-zA-Z0-9][a-zA-Z0-9 _.-]*$/, 'Use letters, digits, spaces, and _.- only'),
  description: z.string().max(512).optional(),
});

type GroupFormValues = z.infer<typeof groupSchema>;

interface AppGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: AppGroupData) => Promise<void>;
  /** When set, edit this group in place; otherwise create. */
  group?: AppGroup | null;
  /** The tenant's apps — the member picker source. */
  apps: AccessApp[];
}

const AppGroupModal = ({ isOpen, onClose, onSave, group = null, apps }: AppGroupModalProps) => {
  const editing = !!group;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<GroupFormValues>({
    resolver: zodResolver<GroupFormValues>(groupSchema),
    defaultValues: { name: group?.name ?? '', description: group?.description ?? '' },
  });

  const [picked, setPicked] = useState<string[]>(() => (group ? group.members.map((m) => m.id) : []));
  const [query, setQuery] = useState('');

  const reset = () => {
    setPicked([]);
    setQuery('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const submit = handleSubmit(async (values) => {
    await onSave({
      name: values.name,
      description: values.description || undefined,
      appIds: picked,
    });
    reset();
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={editing ? 'Edit Application Group' : 'New Application Group'}
      footer={
        <div className="flex justify-end space-x-2">
          <button type="button" className="btn-secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={submit} disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : editing ? 'Save Changes' : 'Create Group'}
          </button>
        </div>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <AccessFormField label="Name" htmlFor="grp-name" required error={errors.name?.message}>
          <input id="grp-name" className="input-base w-full" placeholder="Finance apps" {...register('name')} />
        </AccessFormField>

        <AccessFormField label="Description" htmlFor="grp-desc" error={errors.description?.message}>
          <input id="grp-desc" className="input-base w-full" {...register('description')} />
        </AccessFormField>

        <AccessFormField
          label="Member applications"
          htmlFor="grp-apps-search"
          hint={picked.length > 0 ? `${picked.length} app${picked.length === 1 ? '' : 's'} selected.` : 'Pick the apps in this group.'}
        >
          <input
            id="grp-apps-search"
            className="input-base w-full mb-1"
            placeholder="Search applications…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="max-h-48 overflow-auto rounded border border-gray-200">
            {apps.length === 0 ? (
              <p className="p-2 text-xs text-gray-400">No applications defined yet.</p>
            ) : (
              apps
                .filter((a) => {
                  const q = query.trim().toLowerCase();
                  return !q || a.name.toLowerCase().includes(q) || (a.display_name ?? '').toLowerCase().includes(q);
                })
                .map((a) => (
                  <label key={a.id} className="flex items-center gap-2 px-2 py-1 text-sm hover:bg-blue-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={picked.includes(a.id)}
                      onChange={() =>
                        setPicked((s) => (s.includes(a.id) ? s.filter((x) => x !== a.id) : [...s, a.id]))
                      }
                    />
                    <span className="flex-1 truncate">{a.display_name || a.name}</span>
                    <span className="text-xs text-gray-400">{a.app_type}</span>
                  </label>
                ))
            )}
          </div>
        </AccessFormField>
      </form>
    </Modal>
  );
};

export default AppGroupModal;
